import * as R from 'ramda';
import { Functional as F, LatLng, ImageUpload, MapNote, Feature } from '@wayfinder/vms-base';
import * as LocusLabsMap from '@wayfinder/vms-map';
import * as VmsState from '@wayfinder/vms-state';
import { AsyncParser } from '@json2csv/whatwg';
import * as LliamUtilities from '@wayfinder/lliam-utilities';
import appInsights from '../utils/appInsights';

import JSZip from 'jszip';
import FileSaver from 'file-saver';

import {
    BANNER_COLORS,
    BANNER_ICONS,
    LOGIN_ISSUES,
    POI_SECONDARY_CSV_FIELDS,
    MENUS,
    POI_CSV_FIELDS,
    ERRORCODES,
} from './constants';
import * as Cognito from './cognito';
import * as Telemetry from './telemetry.js';
import * as BulkEditsReducer from './reducers/bulk-edits-reducer';
import * as DataStatusReducer from './reducers/data-status-reducer';
import * as MapNotesReducer from './reducers/map-notes-reducer';
import * as ModalReducer from './reducers/modal-reducer';
import * as PoiStatusReducers from './reducers/poi-status-reducers';
import * as PublishStatusReducer from './reducers/publish-status-reducer';
import * as Routing from './reducers/routing';
import * as SessionReducer from './reducers/session-reducer';
import * as ViewReducer from './reducers/view-reducer';
import * as ModeReducer from './reducers/mode-reducer';
import { readUploadedFiles } from '../browser/browser-upload';

const { NEW_PASSWORDS_MUST_MATCH } = LOGIN_ISSUES;

// ==================================================================================================================
//
// Selectors that need data from multiple reducers
//
// ==================================================================================================================

/*
 * Are we navigating away from a modified entity?
 * @sig isNavigatingAwayFromDirtyEntity :: (State, Location) -> Boolean
 */
const isNavigatingAwayFromDirtyEntity = (state, newLocation) =>
    Routing.Selectors.isNavigatingAwayFromEntity(state.location, newLocation) &&
    PoiStatusReducers.Selectors.isCurrentEntityDirty(state);

/*
 * Is the newLocation a different venue -- even though the current venue has unsaved changes?
 * @sig isVenueCurrentlySaving :: (State, Location) -> Boolean
 */
const isNavigatingAwayFromDirtyVenue = (state, newLocation) =>
    Routing.Selectors.isNavigatingToNewVenue(state.location, newLocation) &&
    PoiStatusReducers.Selectors.isSaving(state);

// ==================================================================================================================
//
// Location selectors
//
// ==================================================================================================================

/*
 * Show the venue embedded in the current location
 */
const goToVenue = async (dispatch, getState) => {
    dispatch(selectVenue(Routing.Selectors.getLocationVenueId(getState())));
};

/*
 * Show the Venues page
 */
const goToVenues = async dispatch => {
    dispatch(VmsState.LandscapeState.actionCreators.loadVenueSummaries());
};

const goToCommandLineTools = async dispatch => {
    const venueSummaries = await dispatch(VmsState.LandscapeState.actionCreators.loadVenueSummaries());
    dispatch(VmsState.LandscapeState.actionCreators.setVenueDropdowns(venueSummaries));
};

/*
 * Show the Maps On Digitial Display Wizard if correct permissions
 */
const goToModWizard = (dispatch, getState) => {
    const hasMapsOnDisplayWritePermission = SessionReducer.Selectors.hasWritePermissionToResource(
        '*',
        LliamUtilities.CONSTANTS.MAPS_ON_DISPLAY,
        getState(),
    );
    if (!hasMapsOnDisplayWritePermission) {
        dispatch(Routing.Actions.goToErrorPage(ERRORCODES.VENUE_NOT_FOUND));
    }
};

/*
 * Show the VisitorInsights page if the user has correct permissions
 */
const goToVisitorInsights = (dispatch, getState) => {
    const hasPeopleDashboardsPermission = SessionReducer.Selectors.hasAtLeastReadPermissionToResource(
        LliamUtilities.CONSTANTS.PEOPLE_INSIGHT_DASHBOARDS,
        getState(),
    );
    if (!hasPeopleDashboardsPermission) {
        dispatch(Routing.Actions.goToErrorPage(ERRORCODES.UNKNOWN_ERROR));
    }
};

/*
 * Enter the Spatial Insights (Looker) page: gate on permission, then fetch a
 * freshly signed embed URL scoped to the caller venues (PBI 1610410).
 * Impure: calls the people-insights API.
 */
const goToSpatialInsights = async (dispatch, getState) => {
    const hasSpatialInsightsPermission = SessionReducer.Selectors.hasAtLeastReadPermissionToResource(
        LliamUtilities.CONSTANTS.SPATIAL_INSIGHT_DASHBOARDS,
        getState(),
    );
    const { featureFlags = {} } = getState();
    // The feature ships behind the flag (no tab without it): entering /spatialinsights
    // directly without it behaves like an unknown path and lands on the login screen.
    // With the flag on, permission/fetch failures stay on the page with the in-page
    // error message.
    const featureEnabled = !!featureFlags.spatialInsights;
    if (!featureEnabled || !hasSpatialInsightsPermission) {
        dispatch(
            featureEnabled
                ? SessionReducer.Actions.setLookerEmbed({ error: 'forbidden' })
                : Routing.Actions.goToLogin(),
        );
        return;
    }
    try {
        const embed = await VmsState.CanonicalLoader.getLookerEmbedConfig();
        dispatch(SessionReducer.Actions.setLookerEmbed(embed));
    } catch (error) {
        appInsights.trackException(error);
        dispatch(
            featureEnabled
                ? SessionReducer.Actions.setLookerEmbed({ error: 'unavailable' })
                : Routing.Actions.goToLogin(),
        );
    }
};

/*
 * redux-first-router callback to intervene before the location is actually changed
 */
const beforeRouteChange = (dispatch, getState, { action: location }) => {
    const currentState = getState();

    // Warning navigation modals
    const showConfirmationModal = isNavigatingAwayFromDirtyEntity(currentState, location);
    const showVenueAlert = isNavigatingAwayFromDirtyVenue(currentState, location);

    if (location.type !== Routing.ROUTE_TYPES.ERROR_PAGE) {
        if (showConfirmationModal) dispatch(ViewReducer.Thunks.showConfirmationModal(currentState.location));
        else if (showVenueAlert) dispatch(ModalReducer.Thunks.showVenueChangeAlert(currentState.location));
    }

    // Always deselect all features when navigating away from an entity
    const deselectAllFeatures = Routing.Selectors.isNavigatingAwayFromEntity(currentState.location, location);
    if (deselectAllFeatures) dispatch(LocusLabsMap.actions.deselectAllFeatures());
};

/*
 * redux-first-router callback to intervene after the location is changed
 */
const afterRouteChange = (dispatch, getState) => {
    const { location, mapNotes } = getState();
    const noteSelected = Routing.Selectors.getLocationNoteId(getState());
    possiblyZoomToPoi(dispatch, getState);
    possiblyZoomToMapNote(dispatch, getState);
    if (noteSelected && mapNotes.sheetVersion === '' && !R.includes('_NEW', noteSelected)) {
        dispatch(editMapNote(noteSelected, location));
    }
    appInsights.trackPageView({ name: location.type, properties: { ...location.payload, ...location.query } });
};

/*
 * Zoom to the current location's "poi" --  if it has one and we're not already there
 */
const possiblyZoomToPoi = (dispatch, getState) => {
    // :: Location -> ()
    const possiblyZoom = F.pipeWhileNotNil([
        Routing.Selectors.getLocationPoiIdIfChanged, // -> "poiId"|undefined
        VmsState.LandscapeState.selectors.findEntityByPoiId, // -> Entity|undefined
        R.prop('id'), // -> Entity.id|undefined
        LocusLabsMap.actions.zoomToEntity, // -> dispatch thunk
        dispatch, // -> ()
    ]);

    return possiblyZoom(getState());
};

/*
 * Zoom to the current location's map note -- if it has one and we're not already there
 */
const possiblyZoomToMapNote = (dispatch, getState) => {
    // :: Location -> ()
    const possiblyZoom = F.pipeWhileNotNil([
        Routing.Selectors.getLocationMapNoteIdIfChanged, // -> MapNoteId|undefined
        MapNotesReducer.Selectors.getMapNotelet(getState()), // -> MapNote|undefined
        LocusLabsMap.actions.zoomToMapNote, // -> dispatch thunk
        dispatch, // -> ()
    ]);
    return possiblyZoom(getState());
};

// ==================================================================================================================

/*
 * MultiThunk
 * Redux-thunk middleware dispatcher to initialize the app using a "rehydrated" session from Cognito
 *
 * Get the user's session from Cognito and either:
 *   - create a loader and go to the initial route OR
 *   - go to the login page
 *
 * Impure: reads Cognito data; initializes loader
 * @sig initializeApp :: () -> DispatchFunction -> Promise ()
 */
const initializeApp = () => async dispatch => {
    Routing.finalizeRouting({
        venuesThunk: goToVenues,
        venueThunk: goToVenue,
        cmdThunk: goToCommandLineTools,
        modWizardThunk: goToModWizard,
        visitorInsightsThunk: goToVisitorInsights,
        spatialInsightsThunk: goToSpatialInsights,
        beforeRouteChange,
        afterRouteChange,
    });

    const userData = await dispatch(SessionReducer.Thunks.attemptRehydrate());
    if (userData) dispatch(ModalReducer.Thunks.setUserDataValues(userData));
};

/*
 * Redux-thunk middleware dispatcher to initialize the app using the user's credentials:
 *
 * Authenticate the user with Cognito and either
 *   - initialize the loader and go to the Venues page
 *   - fail
 *
 * Impure: reads from S3, initializes LandscapeState
 * @sig attemptLogin :: Object -> DispatchFunction -> Promise ()
 */
const attemptLogin = credentials => async (dispatch, getState) => {
    try {
        const sessionInfo = await Cognito.authenticateUser(credentials.email, credentials.password);
        const { loggedIn, userData } = sessionInfo;

        if (loggedIn) {
            await SessionReducer.Thunks.initializationSucceeded(dispatch, userData);
            dispatch(SessionReducer.Actions.clearBanner());
            dispatch(SessionReducer.Actions.loginSuccess());

            const hasVenuesReadPermission = SessionReducer.Selectors.hasAtLeastReadPermissionToResource(
                LliamUtilities.CONSTANTS.OBJECTSTORES,
                getState(),
            );
            const hasMapsOnDisplayWritePermission = SessionReducer.Selectors.hasWritePermissionToResource(
                '*',
                LliamUtilities.CONSTANTS.MAPS_ON_DISPLAY,
                getState(),
            );

            if (hasVenuesReadPermission) {
                dispatch(Routing.Actions.goToVenues());
                dispatch(ViewReducer.Actions.setSelectedMenu(MENUS.VENUES));
            }
            if (!hasVenuesReadPermission && hasMapsOnDisplayWritePermission) {
                dispatch(Routing.Actions.goToModWizard());
                dispatch(ViewReducer.Actions.setSelectedMenu(MENUS.MOD_WIZARD));
            }
            if (!hasVenuesReadPermission && !hasMapsOnDisplayWritePermission) {
                dispatch(Routing.Actions.goToImportExport());
                dispatch(ViewReducer.Actions.setSelectedMenu(MENUS.IMPORT_EXPORT));
            }

            return userData;
        }
        if (sessionInfo.code === Cognito.NEW_PASSWORD_REQUIRED) {
            dispatch(SessionReducer.Actions.loginFailed());
            dispatch(SessionReducer.Actions.showRegisterUser());
            return;
        }
        dispatch(SessionReducer.Actions.loginFailed());
        dispatch(
            SessionReducer.Actions.messageBanner(
                sessionInfo.message,
                BANNER_COLORS.RED,
                BANNER_ICONS.FAIL,
                sessionInfo.code,
            ),
        );
    } catch (e) {
        dispatch(SessionReducer.Actions.messageBanner(e.message, BANNER_COLORS.RED, BANNER_ICONS.FAIL));
        dispatch(SessionReducer.Actions.loginFailed());
    }
};

const logout = venueId => async dispatch => {
    await dispatch(SessionReducer.Thunks.logout());
    dispatch(PublishStatusReducer.Actions.clearStatusInterval(venueId));
    dispatch(VmsState.LandscapeState.actionCreators.reset());
    dispatch(SessionReducer.Actions.resetApp());
    dispatch(Routing.Actions.goToLogin());
};

// Check if the values for email, firstName, or lastName have changed
const getProfileValueChanges = state => {
    const userData = SessionReducer.Selectors.getUserData(state);
    return ModalReducer.Selectors.getProfileChanges(state, userData);
};

/*
 * MultiThunk
 * Updates the user attributes through the session reducer
 * TODO: show the profile modal through the view reducer
 * @sig updateUserAttributes :: () -> Dispatch Function -> Boolean
 */
const updateUserAttributes = () => async (dispatch, getState) => {
    const { email, firstName, lastName } = getProfileValueChanges(getState());

    const result = await dispatch(SessionReducer.Thunks.updateUserAttributes(email, firstName, lastName));
    const userData = SessionReducer.Selectors.getUserData(getState());
    dispatch(ModalReducer.Thunks.setUserDataValues(userData));

    if (result && email) {
        dispatch(ModalReducer.Thunks.showVerifyEmailModal(verifyEmail));
        return result;
    }

    if (result) {
        const handleClose = () => dispatch(closeModal());
        dispatch(ModalReducer.Thunks.showSuccessModal(handleClose, 'Your profile was successfully updated.'));
    }

    return result;
};

/*
 * MultiThunk
 * Verify the users email then show the success alert
 * @sig verifyEmail :: () -> DispatchFunction -> Boolean
 */
const verifyEmail = () => async (dispatch, getState) => {
    const values = ModalReducer.Selectors.getModalTextValues(getState());
    const verified = await dispatch(SessionReducer.Thunks.verifyUserEmail(values.code));

    if (verified) {
        const handleClose = () => dispatch(closeModal());
        dispatch(ModalReducer.Thunks.showSuccessModal(handleClose, 'Your profile was successfully updated.'));
        return true;
    }
    return false;
};

/*
 * MultiThunk
 * Changes the users' password
 * @sig changePassword :: Object -> DispatchFunction -> ()
 */
const changePassword = () => async (dispatch, getState) => {
    const values = ModalReducer.Selectors.getModalTextValues(getState());
    if (values.newPassword !== values.newPasswordConfirmed) {
        dispatch(SessionReducer.Actions.messageBanner(NEW_PASSWORDS_MUST_MATCH, BANNER_COLORS.RED, BANNER_ICONS.FAIL));
        return;
    }

    const changed = await dispatch(SessionReducer.Thunks.changePassword(values.oldPassword, values.newPassword));

    if (changed) {
        const handleClose = () => dispatch(closeModal());
        dispatch(ModalReducer.Thunks.showSuccessModal(handleClose, 'Your password was successfully updated.'));
    }
};

/*
 * MultiThunk
 * Clear the values and close whatever modal is visible
 * @sig closeModal :: () => DispatchFunction -> ()
 */
const closeModal = () => dispatch => {
    dispatch(SessionReducer.Actions.clearBanner());
    dispatch(ViewReducer.Actions.setSelectedSubmenu(null));
    dispatch(ModalReducer.Actions.hideModal());
    dispatch(ModalReducer.Actions.clearModalTextValues());
};

/*
 * MultiThunk
 * Show the profile inputs
 * 'handleSubmit' and 'handleClose' are multi thunks
 */
const showProfileModal = () => dispatch => {
    const handleSubmit = () => dispatch(updateUserAttributes());
    const handleClose = () => dispatch(closeModal());
    dispatch(ModalReducer.Thunks.showProfileModal(handleSubmit, handleClose));
};

/*
 * MultiThunk
 * Show the password inputs
 * 'handleSubmit' and 'handleClose' are multi thunks
 */
const showPasswordModal = () => dispatch => {
    const handleSubmit = () => dispatch(changePassword());
    const handleClose = () => dispatch(closeModal());
    dispatch(ModalReducer.Thunks.showPasswordModal(handleSubmit, handleClose));
};

/*
 * MultiThunk
 * Show the support inputs
 * Opens the users mail to support@locuslabs.com
 * 'handleClose' is a multi thunk
 */
const showSupportModal = () => dispatch => {
    const handleSubmit = () => window.open('mailto:support@locuslabs.com');
    const handleClose = () => dispatch(closeModal());
    dispatch(ModalReducer.Thunks.showSupportModal(handleSubmit, handleClose));
};

// Tracks the venue the user currently has selected so a delayed map-note response for a
// venue navigated away from can be dropped instead of dispatched into the new venue's state.
let lastVenueId;
const isCurrentVenueId = venueId => venueId === lastVenueId;

const loadMapNotes = async (venueId, dispatch) => {
    try {
        const [mapNoteTotals, mapNotelets] = await Promise.all([
            VmsState.CanonicalLoader.getMapNoteTotals(venueId),
            VmsState.CanonicalLoader.getMapNotelets(venueId),
        ]);

        if (!isCurrentVenueId(venueId)) return;

        dispatch(MapNotesReducer.Actions.setTotals(mapNoteTotals));
        dispatch(MapNotesReducer.Actions.setMapNotelets(mapNotelets));
    } catch {
        Telemetry.recordMapNoteLoadFailure(venueId);
    }
};

/*
 * MultiThunk
 * Redux-thunk middleware dispatcher to download the data for a specific venue
 * Impure: downloads lots of data; updates Loki
 * @sig selectVenue :: VenueId -> DispatchFunction -> Promise ()
 */
const selectVenue = venueId => async (dispatch, getState) => {
    // only once per venue!
    if (isCurrentVenueId(venueId)) return;

    dispatch(PublishStatusReducer.Actions.clearStatusInterval(lastVenueId));

    lastVenueId = venueId;
    dispatch(MapNotesReducer.Actions.setTotals({ open: 0, resolved: 0 }));
    dispatch(MapNotesReducer.Actions.setMapNotelets([]));

    await dispatch(VmsState.LandscapeState.actionCreators.reset());

    dispatch(PublishStatusReducer.Thunks.beginStatusInterval(venueId));
    await dispatch(VmsState.LandscapeState.actionCreators.loadVenueSummaries());

    // TODO: we need venueSummary only for the hack that the venueId and the venueSummary's id might be different
    // see fakeEnrichVenueSummaryData for more information
    const venueSummary = VmsState.LandscapeState.selectors.getVenueSummary(venueId);

    if (venueSummary) {
        const { featureFlags = {} } = getState();
        let startVenueAction;
        if (featureFlags.translatedVenues) {
            startVenueAction = VmsState.LandscapeState.actionCreators.startVenue(
                venueSummary.id,
                venueSummary.locale,
                venueSummary.code.toLowerCase(),
            );
        } else {
            startVenueAction = VmsState.LandscapeState.actionCreators.startVenue(venueSummary.id);
        }

        const [success] = await Promise.all([
            dispatch(startVenueAction),
            dispatch(PublishStatusReducer.Thunks.getPublishAssetStatus(venueId)),
            dispatch(PublishStatusReducer.Thunks.getLastPublishSuccess(venueId)),
            dispatch(PublishStatusReducer.Thunks.checkPublishFreeze(venueId)),
        ]);

        const mapNotePermissions = SessionReducer.Selectors.hasReadPermissionToResource(
            venueId,
            LliamUtilities.CONSTANTS.MAPNOTES,
            getState(),
        );
        if (mapNotePermissions) loadMapNotes(venueId, dispatch);

        const venue = VmsState.LandscapeState.selectors.getVenue(venueId, getState());

        const zoomToCenter = success && venue && VmsState.LandscapeState.selectors.isVenueV5(venueId, getState());

        if (zoomToCenter) dispatch(LocusLabsMap.actions.zoomToLocationOrVenueCenter(venueId, getState().location));
    } else {
        dispatch(Routing.Actions.goToErrorPage(ERRORCODES.VENUE_NOT_FOUND));
    }
};

const selectStructure = (selectedStructure, location) => async (dispatch, getState) => {
    const levelId = VmsState.LandscapeState.selectors.defaultLevelIdForStructure(selectedStructure.id, getState());

    const newLocation = F.mergeWithProp(
        'query',
        {
            levelId,
            structureId: selectedStructure.id,
            ordinal: String(VmsState.LandscapeState.selectors.ordinalForLevel(levelId, getState()).ordinalNumber),
        },
        location,
    );

    dispatch(newLocation);
    dispatch(LocusLabsMap.actions.showLevel(levelId));
    dispatch(LocusLabsMap.actions.zoomToStructure(selectedStructure.venueId, selectedStructure.id));
};

const selectLevel = (selectedLevel, location) => (dispatch, getState) => {
    const newLocation = F.mergeWithProp(
        'query',
        {
            levelId: selectedLevel.id,
            structureId: VmsState.LandscapeState.selectors.getStructureFromLevelId(selectedLevel.id, getState()).id,
            ordinal: String(
                VmsState.LandscapeState.selectors.ordinalForLevel(selectedLevel.id, getState()).ordinalNumber,
            ),
        },
        location,
    );

    dispatch(newLocation);
    dispatch(LocusLabsMap.actions.showLevel(selectedLevel.id));
    Telemetry.recordLevelChange(selectedLevel.venueId, selectedLevel.id);
};

/*
 * MultiThunk
 * Get an "unoccupied" polygon feature from the supplied feature id
 * Create a new entity to associate with the unoccupied polygon
 * Highlight the features in the UI, and start the new poi flow
 * Optionally zoom to the polygon
 * @sig openLocation :: (FeatureId, Location, Boolean) -> Dispatch -> ()
 */
const openLocation =
    (featureId, location, shouldZoom = false) =>
    dispatch => {
        const polygonFeature = VmsState.LandscapeState.selectors.getFeature(featureId);
        const entity = dispatch(VmsState.LandscapeState.actionCreators.openPolygon(polygonFeature));

        dispatch(LocusLabsMap.actions.selectFeaturesForEntities([entity.id]));
        dispatch(PoiStatusReducers.Actions.startNewPoi(entity.venueId, entity));
        dispatch(Routing.Actions.goToPoi(entity.venueId, entity.uuid, location.query));
        dispatch(PoiStatusReducers.Actions.closeFeature(polygonFeature));

        if (shouldZoom) dispatch(LocusLabsMap.actions.zoomToEntity(entity.id));
    };

/*
 * MultiThunk
 * Update a single or multiple entities
 * The changes object differs depending on whether this is a bulk update
 * @sig updateEntities :: (VenueId, [String], Object, Boolean, [Object]) -> Boolean
 */
const updateEntities = (venueId, uuids, changes, isBulkUpdate, updatedPoiCoords, entities) => async dispatch => {
    if (isBulkUpdate) dispatch(BulkEditsReducer.Actions.savingBulkEdits());
    else dispatch(DataStatusReducer.Actions.beginSavePoi(venueId, uuids));
    try {
        const action = PoiStatusReducers.Thunks.updateEntities(
            venueId,
            uuids,
            changes,
            isBulkUpdate,
            updatedPoiCoords,
            entities,
        );
        const updatedEntities = await dispatch(action);

        if (isBulkUpdate) dispatch(BulkEditsReducer.Actions.bulkSaveComplete());
        else dispatch(DataStatusReducer.Actions.savePoiSuccess(uuids, updatedEntities[0]));
        return true;
    } catch {
        if (isBulkUpdate) dispatch(BulkEditsReducer.Actions.bulkSaveFailure());
        else dispatch(PoiStatusReducers.Actions.savePoiFailure(uuids, 'Failure saving POI'));
        return false;
    }
};

/*
 * MultiThunk
 * Create a point entity or polygon entity
 * @sig createNewPoi :: (VenueId, Object, Boolean, Feature) -> Boolean
 */
const createNewPoi = (venueId, poiData, openLocation, oldFeature) => async (dispatch, getState) => {
    dispatch(DataStatusReducer.Actions.beginCreatePoi(venueId));
    dispatch(DataStatusReducer.Actions.beginSavePoi(venueId, `${venueId}_NEW`));

    Telemetry.recordCreatePoi(venueId, poiData);

    const isV5 = VmsState.LandscapeState.selectors.isVenueV5(venueId, getState());
    try {
        const action = PoiStatusReducers.Thunks.createEntity(venueId, poiData, openLocation, oldFeature);
        const newEntity = await dispatch(action);

        dispatch(DataStatusReducer.Actions.createPoiSuccess(venueId));
        if (isV5) dispatch(Routing.Actions.goToPoi(venueId, newEntity.uuid, { view: 'map' }));
        else dispatch(Routing.Actions.goToPoi(venueId, newEntity.uuid));
        dispatch(DataStatusReducer.Actions.savePoiSuccess(`${venueId}_NEW`, newEntity));
        return true;
    } catch (e) {
        const errorMessage = typeof e === 'string' ? e : 'Unknown error saving new POI';
        dispatch(PoiStatusReducers.Actions.createPoiFailure(venueId, errorMessage));
        dispatch(PoiStatusReducers.Actions.savePoiFailure(`${venueId}_NEW`, 'Failure saving new POI'));
        return false;
    }
};

/*
 * MultiThunk
 * Create a new Entity on the map
 * @sig addEntity :: ([Lnt, Lat], Object) -> (Dispatch, State) -> ()
 */
export const addEntity = (lngLat, oldLocation) => (dispatch, getState) => {
    dispatch(clearUnsavedMapNote());
    const venueId = Routing.Selectors.locLocationVenueId(oldLocation);
    const latLng = LatLng.fromArray(lngLat);
    const levelId = LocusLabsMap.selectors.levelAtLatLng(getState(), venueId, latLng, true);
    const entity = VmsState.LandscapeState.selectors.createIdForEntity(
        venueId,
        VmsState.LandscapeState.selectors.createDefaultEntity(
            venueId,
            levelId,
            latLng,
            LocusLabsMap.selectors.getCurrentOrdinalNumber(getState()),
            true,
            false,
            'Outside',
            getState(),
        ),
    );
    dispatch(
        VmsState.LandscapeState.actionCreators.addEntity({
            entity,
            showBadge: true,
            showName: false,
            levelId,
            latLng,
        }),
    );
    dispatch(LocusLabsMap.actions.selectFeaturesForEntities([entity.id]));
    dispatch(PoiStatusReducers.Actions.startNewPoi(entity.venueId, entity));
    dispatch(Routing.Actions.goToPoi(entity.venueId, entity.uuid, oldLocation.query));

    Telemetry.recordAddPoi(venueId);
};

export const addMapNote = (lngLat, oldLocation) => (dispatch, getState) => {
    dispatch(clearUnsavedMapNote());
    const venueId = Routing.Selectors.locLocationVenueId(oldLocation);
    const latLng = LatLng.fromArray(lngLat);
    const tempNewMapNote = [MapNote.MapNote.createTempMapNotelet(latLng, venueId)];
    const currentMapNotes = getState().mapNotes.mapNotelets;
    dispatch(MapNotesReducer.Actions.setSheetVersion(''));
    dispatch(MapNotesReducer.Actions.setMapNotelets(R.concat(currentMapNotes, tempNewMapNote)));
    dispatch(Routing.Actions.goToMapNote(venueId, `${venueId}_NEW`, oldLocation.query));
    dispatch(MapNotesReducer.Actions.setSelectedMapNote({}));
    dispatch(MapNotesReducer.Actions.setSheetVersion('new'));

    Telemetry.recordAddMapNote(venueId);
};

const getOpenOrClosedEntity = (venueId, id) =>
    VmsState.LandscapeState.selectors.findEntity({ venueId, id }) ||
    VmsState.LandscapeState.selectors.findEntity({ venueId, id: `#${id}` });

/*
 * MultiThunk
 * Fires an action to zoom into the entity and pull up the POI Sheet with its information
 * @sig addEntity :: (Object, [Lnt, Lat], Object) -> dispatch -> ()
 */
const editEntity = (entityId, oldLocation, rawFeature) => (dispatch, getState) => {
    const poiPath = R.path(['payload', 'poiId'], oldLocation);
    if (poiPath && R.includes('_NEW', poiPath)) return;
    if (
        !R.isEmpty(MapNotesReducer.Selectors.getUpdatedFields(getState())) ||
        !R.isEmpty(MapNotesReducer.Selectors.getComment(getState()))
    ) {
        dispatch(MapNotesReducer.Actions.setShowExitMapNoteModal(true));
    } else {
        onMapNoteSheetClose()(dispatch, getState);

        const venueId = Routing.Selectors.locLocationVenueId(oldLocation);
        const entity = getOpenOrClosedEntity(venueId, entityId);
        const isUnoccupied = !entity && F.pathStringEq('properties.category', 'unoccupied', rawFeature);
        const rawFeatureId = F.pathString('properties.subid', rawFeature);

        if (isUnoccupied) dispatch(openLocation(rawFeatureId, oldLocation, true));

        if (!entity) return;

        dispatch(ViewReducer.Actions.mapFeatureClicked(entity.id));
        dispatch(Routing.Actions.goToPoi(venueId, entity.uuid, oldLocation.query));
    }
};

/*
 * MultiThunk
 * Fires an action to pull up the MapNote Sheet with its information
 * @sig editMapNote :: (Object, [Lnt, Lat]) -> (dispatch, getState) -> ()
 */
const editMapNote = (mapNoteId, oldLocation) => async dispatch => {
    const prevNotePath = R.path(['prev', 'payload', 'noteId'], oldLocation);
    if (mapNoteId < 0 || prevNotePath === mapNoteId) return;
    dispatch(clearUnsavedMapNote());
    const venueId = Routing.Selectors.locLocationVenueId(oldLocation);
    dispatch(Routing.Actions.goToVenue(venueId, { view: 'map' }));
    dispatch(MapNotesReducer.Actions.setSheetVersion('loading'));
    const mapNote = await MapNotesReducer.Thunks.findSelectedMapNote(venueId, mapNoteId);
    if (!mapNote) {
        dispatch(MapNotesReducer.Actions.setSheetVersion(''));
        return;
    }
    dispatch(MapNotesReducer.Actions.setSelectedMapNote(mapNote));
    dispatch(MapNotesReducer.Actions.setSheetVersion('view'));
    dispatch(ViewReducer.Actions.mapFeatureClicked(mapNote.id));
    dispatch(Routing.Actions.goToMapNote(venueId, mapNote.id, oldLocation.query));
};

/*
 * MultiThunk
 * Move entity on the map
 * @sig moveEntity :: ([Lnt, Lat], Object) -> (Dispatch, State) -> ()
 */
export const moveEntity = (lngLat, oldLocation) => (dispatch, getState) => {
    const poiId = R.path(['payload', 'poiId'], oldLocation);
    const venueId = Routing.Selectors.locLocationVenueId(oldLocation);
    const entity = getOpenOrClosedEntity(venueId, R.last(R.split('_', poiId)));
    const latLng = LatLng.fromArray(lngLat);
    const levelId = LocusLabsMap.selectors.levelAtLatLng(getState(), venueId, latLng, false);
    const level = VmsState.LandscapeState.selectors.getLevel(levelId, getState());
    const ordinalNumber = LocusLabsMap.selectors.getCurrentOrdinalNumber(getState());
    // the floor should be`${ ordinalNumber } ${ level.details }` as it is when adding a new POI
    dispatch(PoiStatusReducers.Actions.editPoi(entity.uuid, { floor: `${ordinalNumber} ${level.details}` }));
    dispatch(
        VmsState.LandscapeState.actionCreators.updateEntity({
            entity,
            showBadge: true,
            showName: false,
            latLng,
            levelId,
        }),
    );
    dispatch(ModeReducer.Actions.changeMovePoiMode('save'));
    dispatch(PoiStatusReducers.Actions.updatePoiCoords(latLng));
};

/*
 * MultiThunk
 * Check if either a MapNote or POI sheet is open and close or open a model before switching
 * @sig clearUnsavedEntity :: () -> (dispatch, getState)
 */
const clearUnsavedMapNote = () => (dispatch, getState) => {
    const { mapNotelets, updatedFields } = getState().mapNotes;
    if (R.find(R.propEq(-1, 'id'))(mapNotelets)) {
        if (R.isEmpty(updatedFields)) dispatch(removeTempMapNotelet(mapNotelets));
        else dispatch(MapNotesReducer.Actions.setShowExitMapNoteModal(true));
    }
    dispatch(MapNotesReducer.Actions.setSheetVersion(''));
    dispatch(MapNotesReducer.Actions.setSelectedMapNote({}));
    const poiId = R.path(['payload', 'poiId'], getState().location);
    if (poiId && R.includes('_NEW', poiId)) {
        dispatch(PoiStatusReducers.Actions.revertAllEdits());
    }
};

/*
 * MultiThunk
 * Check what on the map has been clicked and fire the correct action
 * @sig checkForUnsavedEntity :: () -> (dispatchFunction, getState)
 */
const checkForUnsavedEntity = () => (dispatch, getState) => {
    const { comment, updatedFields } = getState().mapNotes;
    const prevEntityPath = R.path(['location', 'prev', 'payload'], getState());
    if (R.has('noteId', prevEntityPath) && (!R.isEmpty(comment) || !R.isEmpty(updatedFields))) {
        // console.log('noteId')
    }
    if (R.has('poiId', prevEntityPath)) {
        // console.log('poiId')
    }
};

/*
 * MultiThunk
 * Check what on the map has been clicked and fire the correct action
 * @sig fireMapClickEvent :: (Object, Object, dispatch, [Lnt, Lat]) -> ()
 */
export const fireMapClickEvent = (mode, event, oldLocation) => dispatch => {
    const determination = LocusLabsMap.selectors.determineMapClickAction(mode, event);
    dispatch(checkForUnsavedEntity());
    /*
        1. Before determining what action to take, check the location to see what you're currently doing
        2. If there is an existing note or POI open, then check to see if edits were made
        2a. If edits were made, pop modal for unsaved changes
        2b. If no edits were made, then continue on to switch statement
        3. If there is no POI or MapNote, assume it is a ghost
        3a. Call the delete function to remove the note, and continue to switch
    */

    switch (determination.eventType) {
        case 'ADD_ENTITY':
            return dispatch(addEntity(event.lngLat, oldLocation));
        case 'EDIT_ENTITY':
            return dispatch(editEntity(determination.entityId, oldLocation, determination.rawFeature));
        case 'ADD_MAP_NOTE':
            return dispatch(addMapNote(event.lngLat, oldLocation));
        case 'EDIT_MAP_NOTE':
            return dispatch(editMapNote(determination.mapNoteId, oldLocation));
        case 'MOVE_ENTITY':
            return dispatch(moveEntity(event.lngLat, oldLocation));
        default:
            break;
    }
};

/*
 * Helper Function
 * Print the exception to the console
 * @sig handleException :: (String)
 */
const handleException = e => console.log(e);

/*
 * Helper Function
 * Get the structureId from the levelId
 * @sig getStructureId :: (Object)
 */
const getStructureId = levelId => {
    const levelList = R.split('-', levelId);
    const level = R.last(levelList);
    const structureList = R.without([level], levelList);
    return R.join('-', structureList);
};

/*
 * MultiThunk
 * Add a text comment to the map note
 * @sig createMapNote :: () -> async (DispatchFunction, State)
 */
const createMapNote = () => async (dispatch, getState) => {
    const { venueId } = getState().location.payload;
    const { email } = getState().session.userData;
    const { mapNotelets, updatedFields, totals } = getState().mapNotes;
    const tempMapNotelet = R.find(R.propEq(-1, 'id'))(mapNotelets);
    const ordinalId = LocusLabsMap.selectors.getCurrentOrdinalNumber(getState());
    const levelId = Routing.Selectors.getLocationLevelId(getState());
    const structureId = levelId ? getStructureId(levelId) : null;
    const createdMapNote = MapNote.MapNote.createNewMapNote(
        updatedFields,
        venueId,
        ordinalId,
        tempMapNotelet,
        levelId,
        structureId,
    );
    dispatch(MapNotesReducer.Actions.setLoading(true));
    try {
        const newMapNote = await VmsState.CanonicalLoader.createMapNote(venueId, email, createdMapNote);
        if (R.has('uploadedImages', updatedFields) && !R.isEmpty(updatedFields.uploadedImages)) {
            const images = updatedFields.uploadedImages;
            const convertImages = async acceptedFiles => {
                const inParallel = R.map(ImageUpload.convertImage, acceptedFiles);
                const createdObjComments = await Promise.all(inParallel);
                return createdObjComments;
            };
            const convertedImages = await convertImages(images);
            dispatch(MapNotesReducer.Actions.setSelectedMapNote(newMapNote));
            await MapNotesReducer.Thunks.addMapNoteCommentImages(convertedImages, venueId, email)(dispatch, getState);
            dispatch(MapNotesReducer.Actions.setLoading(true));
        }
        dispatch(MapNotesReducer.Actions.setTotals(R.assoc('open', totals.open + 1, totals)));
        dispatch(MapNotesReducer.Actions.setMapNoteFilter(MapNote.MapNoteFilter.fromDefault()));
        const newMapNotelet = R.omit(['comments', 'createdBy', 'dueAt', 'isPublic'], newMapNote);
        const newMapNotelets = R.without([tempMapNotelet], R.concat(mapNotelets, [newMapNotelet]));
        dispatch(MapNotesReducer.Actions.setMapNotelets(newMapNotelets));
        const selectedMapNote = await MapNotesReducer.Thunks.findSelectedMapNote(venueId, newMapNote.id);
        dispatch(MapNotesReducer.Actions.setSelectedMapNote(selectedMapNote));
        dispatch(MapNotesReducer.Actions.clearUpdatedFields());
        dispatch(MapNotesReducer.Actions.setSheetVersion('view'));

        Telemetry.recordSaveMapNote(venueId, newMapNote.id);
    } catch (e) {
        handleException(e);
    }
    dispatch(MapNotesReducer.Actions.setLoading(false));
};

/*
 * MultiThunk
 * If closing a mapNoteSheetNew, remove the temporary map note
 * @sig removeTempMapNotelet :: [Object] -> async (DispatchFunction)
 */
const removeTempMapNotelet = mapNotelets => dispatch => {
    const tempMapNote = R.find(R.propEq(-1, 'id'))(mapNotelets);
    dispatch(MapNotesReducer.Actions.setMapNotelets(R.without([tempMapNote], mapNotelets)));
};

/*
 * MultiThunk
 * Steps on move point POI
 * @sig onMovePoi :: () -> async (DispatchFunction)
 */
const onMovePoi = poi => (dispatch, getState) => {
    const { location } = getState();
    const { lat, lng } = location.query;
    const latLng = R.clone(LatLng.fromArray([parseFloat(lng), parseFloat(lat)]));
    if (!R.pathEq('map', ['query', 'view'], location)) {
        dispatch(Routing.Actions.changeView(location, 'map'));
    }
    dispatch(LocusLabsMap.actions.zoomToEntity(poi.id));
    dispatch(ModeReducer.Actions.changeMovePoiMode('move'));
    dispatch(PoiStatusReducers.Actions.lastSavedPoiCoords(latLng));
};

/*
 * MultiThunk
 * Reset the map note fields to default and close the sheet
 * @sig closeMapNoteSheet :: () -> async (DispatchFunction, State)
 */
const onMapNoteSheetClose = () => async (dispatch, getState) => {
    const { venueId } = getState().location.payload;
    const mapNotelets = getState().mapNotes.mapNotelets;
    dispatch(MapNotesReducer.Actions.setSheetVersion(''));

    dispatch(removeTempMapNotelet(mapNotelets));

    dispatch(Routing.Actions.goToVenue(venueId, { view: 'map' }));
    dispatch(MapNotesReducer.Actions.setSelectedMapNote({}));
    dispatch(MapNotesReducer.Actions.clearUpdatedFields());
    dispatch(MapNotesReducer.Actions.setComment(''));
};

const updateStyle = (venueId, accountId, event) => async (dispatch, getState) => {
    const [style] = await readUploadedFiles(event);
    const venueCategory = VmsState.LandscapeState.selectors.getVenueCategory(venueId, getState());
    const convertedStyle = VmsState.convertStyle(JSON.parse(style), venueCategory, venueId, accountId);
    const action = VmsState.LandscapeState.actionCreators.setMapStyleAction(venueId, convertedStyle);
    dispatch(action);
};

const updateTheme = (venueId, event) => async dispatch => {
    let [theme] = await readUploadedFiles(event);
    theme = JSON.parse(theme);
    dispatch(VmsState.LandscapeState.actionCreators.setThemeAction(venueId, theme));

    let features = VmsState.LandscapeState.selectors.getFeatures();
    features = R.map(Feature.updateFeatureFromTheme(theme), features);
    dispatch(VmsState.LandscapeState.actionCreators.addFeaturesAction(venueId, features));
};

const downloadGeojson = venueId => async (dispatch, getState) => {
    const featuresToSource = features => ({
        type: 'FeatureCollection',
        features,
    });

    const levelIds = VmsState.LandscapeState.selectors.levelIdsForVenue(venueId, getState());

    let features = R.map(VmsState.LandscapeState.selectors.featuresForLevel, levelIds);
    features = R.prepend(
        [venueId, VmsState.LandscapeState.selectors.featuresForBackground()],
        R.zip(levelIds, features),
    );

    features = R.map(([levelId, f]) => {
        f = R.map(R.omit(['$loki']), f);
        return [levelId, featuresToSource(f)];
    }, features);

    const generateZip = async zip =>
        new Promise((resolve, reject) => zip.generateAsync({ type: 'blob' }).then(resolve).catch(reject));

    const addFileToZip = ([fileName, result]) => zip.file(`${fileName}.json`, F.stringify(result));

    const zip = new JSZip();
    R.forEach(addFileToZip, features);
    const fileData = await generateZip(zip);
    FileSaver.saveAs(fileData, 'geojson.zip');
};

const downloadPois = (venueId, hasMapSyndicationPermission) => async (dispatch, getState) => {
    const entities = VmsState.LandscapeState.selectors.searchEntities(venueId);
    const openEntities = R.filter(e => e.id && !e.id.includes('#'), entities);

    const enrichEntitiesWithLocation = entity => {
        const position = VmsState.LandscapeState.selectors.positionForEntity(entity.id, getState());
        const enrichedEntity = R.assoc('lat', position.lat, entity);
        return R.assoc('lng', position.lng, enrichedEntity);
    };

    const enrichedEntities = R.map(enrichEntitiesWithLocation, openEntities);
    let downloadableFields = POI_CSV_FIELDS;

    if (!hasMapSyndicationPermission) downloadableFields = POI_SECONDARY_CSV_FIELDS;

    const parser = new AsyncParser({ fields: downloadableFields });

    const csv = await parser.parse(enrichedEntities).promise();

    console.log('---CSV', csv);

    const byteOrderMark = String.fromCharCode(0xfeff);
    const blob = new Blob([byteOrderMark, csv], { contentType: 'text/csv;charset=utf-8' });

    FileSaver.saveAs(blob, `${venueId}-pois.csv`);

    Telemetry.recordDownloadPois(venueId);
};

// ==================================================================================================================

const MultiThunks = {
    attemptLogin,
    openLocation,
    addEntity,
    createNewPoi,
    initializeApp,
    logout,
    selectVenue,
    loadMapNotes,
    selectStructure,
    selectLevel,
    updateEntities,
    updateUserAttributes,
    closeModal,
    showProfileModal,
    showPasswordModal,
    showSupportModal,
    verifyEmail,
    changePassword,
    onMovePoi,
    onMapNoteSheetClose,
    createMapNote,
    updateStyle,
    updateTheme,
    downloadGeojson,
    downloadPois,
};

export { MultiThunks };
